import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { CreateAccessScopeRequest } from '@matech/thebigpos-sdk'

import { TheBigPOSApi } from '../../../../lib/TheBigPOSClient'
import { ACCESS_ITEM_TYPES } from '../../../../components/modals/ModalUserGroupsForm'
import type { UserGroupScopeItem } from '../components/ModalUserGroupsAccess/ModalUserGroupsAccess'

type CreateUserGroupScopeMutationParams = CreateAccessScopeRequest & {
	groupId: string
}

const useUserGroupAccessScope = (
	groupAccessScopes?: UserGroupScopeItem[]
) => {
	const createUserGroupAccessScopeMutation = useMutation({
		mutationFn: async ({
			groupId,
			scopeType,
			branchId,
			userId,
		}: CreateUserGroupScopeMutationParams) => {
			const response = await TheBigPOSApi.createUserGroupAccessScope(
				groupId,
				{
					scopeType,
					branchId,
					userId,
				}
			)

			return response.data
		},
	})

	const deleteUserGroupAccessScopeMutation = useMutation({
		mutationFn: async ({
			groupId,
			scopeId,
		}: {
			groupId: string
			scopeId: string
		}) => {
			const response = await TheBigPOSApi.deleteUserGroupAccessScope(
				groupId,
				scopeId
			)

			return response.data
		},
	})

	const isSubmittingGroupScope =
		createUserGroupAccessScopeMutation.isPending ||
		deleteUserGroupAccessScopeMutation.isPending

	const syncGroupAccessScope = async (
		groupId: string,
		accessScopeItems: UserGroupScopeItem[]
	) => {
		try {
			const currentAccessItems = groupAccessScopes || []
			const newAccessItems = accessScopeItems || []

			const accessItemsToRemove = currentAccessItems.filter(
				(currentItem) =>
					!newAccessItems.some(
						(newItem) => newItem.id === currentItem.id
					)
			)

			const accessItemsToAdd = newAccessItems.filter(
				(newItem) =>
					!currentAccessItems.some(
						(currentItem) => currentItem.id === newItem.id
					)
			)

			await Promise.all([
				...accessItemsToRemove.map((itemToRemove) =>
					deleteUserGroupAccessScopeMutation.mutateAsync({
						groupId,
						scopeId: itemToRemove.id,
					})
				),
				...accessItemsToAdd.map((itemToAdd) =>
					createUserGroupAccessScopeMutation.mutateAsync({
						groupId,
						scopeType: itemToAdd.type,
						branchId:
							itemToAdd.type === ACCESS_ITEM_TYPES.BRANCH
								? itemToAdd.value
								: undefined,
						userId:
							itemToAdd.type === ACCESS_ITEM_TYPES.USER
								? itemToAdd.value
								: undefined,
					})
				),
			])
		} catch (error) {
			captureException(error)
		}
	}

	return {
		syncGroupAccessScope,
		isSubmittingGroupScope,
	}
}

export default useUserGroupAccessScope
